import { useState, useEffect } from "react";
import { supabase } from "../../../../../integrations/supabase/client";
import { Switch } from "../../../../ui/switch";
import { Label } from "../../../../ui/label";
import { Checkbox } from "../../../../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/select";
import { useToast } from "../../../../../hooks/use-toast";
import { Loader2, Clock, RefreshCw } from "lucide-react";
import { format, addDays, addHours, addWeeks } from "date-fns";

interface AutoSyncSettingsProps {
  outletId: string;
  autoSyncEnabled?: boolean;
  autoSyncFrequency?: string;
  autoSyncPlatforms?: string[];
  lastAutoSync?: string | null;
  onUpdated?: () => void;
}

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "every_6_hours", label: "Every 6 hours" },
  { value: "weekly", label: "Weekly" },
];

const PLATFORM_OPTIONS = [
  { value: "google", label: "Google", icon: "🗺️" },
  { value: "facebook", label: "Facebook", icon: "👍" },
  { value: "tripadvisor", label: "TripAdvisor", icon: "✈️" },
];

const AutoSyncSettings = ({
  outletId,
  autoSyncEnabled = false,
  autoSyncFrequency = "daily",
  autoSyncPlatforms = ["google"],
  lastAutoSync = null,
  onUpdated,
}: AutoSyncSettingsProps) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(autoSyncEnabled);
  const [frequency, setFrequency] = useState(autoSyncFrequency);
  const [platforms, setPlatforms] = useState<string[]>(autoSyncPlatforms);

  useEffect(() => {
    setEnabled(autoSyncEnabled);
    setFrequency(autoSyncFrequency);
    setPlatforms(autoSyncPlatforms);
  }, [autoSyncEnabled, autoSyncFrequency, autoSyncPlatforms]);

  const saveSettings = async (updates: Record<string, unknown>) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("outlets")
        .update(updates)
        .eq("id", outletId);

      if (error) throw error;

      toast({
        title: "Auto Sync Updated",
        description: "Settings saved successfully.",
      });
      onUpdated?.();
    } catch (error: any) {
      console.error("Error saving auto sync settings:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (checked: boolean) => {
    setEnabled(checked);
    await saveSettings({ auto_sync_enabled: checked });
  };

  const handleFrequencyChange = async (value: string) => {
    setFrequency(value);
    await saveSettings({ auto_sync_frequency: value });
  };

  const handlePlatformToggle = async (platform: string, checked: boolean) => {
    const updated = checked
      ? [...platforms, platform]
      : platforms.filter((p) => p !== platform);

    if (updated.length === 0) {
      toast({
        title: "Error",
        description: "Select at least one platform",
        variant: "destructive",
      });
      return;
    }

    setPlatforms(updated);
    await saveSettings({ auto_sync_platforms: updated });
  };

  const getNextSync = (): string => {
    if (!enabled) return "—";
    const base = lastAutoSync ? new Date(lastAutoSync) : new Date();
    let next: Date;
    switch (frequency) {
      case "every_6_hours":
        next = addHours(base, 6);
        break;
      case "weekly":
        next = addWeeks(base, 1);
        break;
      default:
        next = addDays(base, 1);
    }
    // If next is in the past, calculate from now
    if (next < new Date()) {
      const now = new Date();
      switch (frequency) {
        case "every_6_hours":
          next = addHours(now, 6);
          break;
        case "weekly":
          next = addWeeks(now, 1);
          break;
        default:
          next = addDays(now, 1);
      }
    }
    return format(next, "dd MMM yyyy, HH:mm");
  };

  return (
    <div className="border border-border rounded-lg p-4 space-y-4 bg-muted/30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Auto Sync Settings</h4>
          {saving && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor={`auto-sync-${outletId}`} className="text-xs text-muted-foreground">
            {enabled ? "Enabled" : "Disabled"}
          </Label>
          <Switch
            id={`auto-sync-${outletId}`}
            checked={enabled}
            onCheckedChange={handleToggle}
          />
        </div>
      </div>

      {enabled && (
        <div className="space-y-4 pt-2">
          {/* Frequency */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Sync Frequency</Label>
            <Select value={frequency} onValueChange={handleFrequencyChange}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQUENCY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Platforms */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Platforms to Monitor</Label>
            <div className="flex flex-wrap gap-3">
              {PLATFORM_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-1.5 text-xs cursor-pointer"
                >
                  <Checkbox
                    checked={platforms.includes(opt.value)}
                    onCheckedChange={(checked) =>
                      handlePlatformToggle(opt.value, !!checked)
                    }
                  />
                  <span>
                    {opt.icon} {opt.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1 border-t border-border">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>Last Sync:</span>
              <span className="font-medium text-foreground">
                {lastAutoSync
                  ? format(new Date(lastAutoSync), "dd MMM yyyy, HH:mm")
                  : "Never"}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span>Next Sync:</span>
              <span className="font-medium text-foreground">{getNextSync()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoSyncSettings;
